import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, CheckCircle2, Camera, ArrowRight } from 'lucide-react';
import PageLayout, { PageCTA } from '../components/PageLayout';
import { IMAGES } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import api from '../api';

const EXP_THUMB = 'https://customer-assets-agu9un31.emergentagent.net/job_industrial-hub-105/artifacts/36plyhct_Image_20260820194116_34_5.jpg';

export default function MissionsPage() {
  const { t, lang } = useLang();
  const p = t('pages.missions');
  const [missions, setMissions] = useState(t('platform.missionsList'));
  useEffect(() => {
    api.get('/missions', { params: { lang } }).then((r) => { if (r.data?.length) setMissions(r.data); }).catch(() => {});
  }, [lang]);
  return (
    <PageLayout crumb={p.breadcrumb} title={p.hero} subtitle={p.sub} heroImage={IMAGES.meeting}>
      {/* Featured experience card */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Link
            to="/experience"
            data-testid="missions-experience-card"
            className="group grid md:grid-cols-5 items-stretch bg-brand-navy rounded-2xl overflow-hidden shadow-2xl hover:shadow-[0_30px_60px_-20px_rgba(11,20,73,0.55)] transition-shadow"
          >
            <div className="md:col-span-2 relative min-h-[240px] md:min-h-full overflow-hidden">
              <img src={EXP_THUMB} alt={p.exp.title} className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/40 via-transparent to-brand-navy md:bg-gradient-to-r md:from-transparent md:to-brand-navy" />
            </div>
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase w-fit">
                <Camera className="w-3.5 h-3.5" /> {p.exp.badge}
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-4">{p.exp.title}</h3>
              <p className="text-white/80 leading-relaxed mb-6 max-w-2xl">{p.exp.body}</p>
              <span className="inline-flex items-center gap-2 self-start px-5 py-3 bg-brand-yellow text-brand-navy font-bold rounded-full text-sm group-hover:gap-3 transition-all">
                {p.exp.cta} <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-10 text-center">{p.includes}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {p.items.map((it, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100 hover:border-brand-yellow transition">
                <div className="shrink-0 w-10 h-10 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-brand-yellow" />
                </div>
                <div className="text-brand-navy font-medium pt-1">{it}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-navy text-center mb-10">{p.upcoming}</h2>
          <div className="space-y-4">
            {missions.map((m, i) => (
              <div key={i} className="flex items-center justify-between p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-brand-yellow hover-lift">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-full bg-brand-navy flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-yellow" />
                  </div>
                  <div>
                    <div className="font-bold text-brand-navy text-xl">{m.city}</div>
                    <div className="text-sm text-gray-500">{m.focus}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-brand-navy font-semibold">
                  <Calendar className="w-4 h-4" /> {m.date}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCTA title={p.ctaTitle} body={p.ctaBody} />
    </PageLayout>
  );
}
